import { useRef } from "react";
import type { Scene } from "../types/story";
import { Illustration, NarrationText, ScenePlayer, sceneProgress } from "./ScenePlayer";
import { useMediaQuery } from "../lib/useMediaQuery";

/**
 * The story as a book.
 *
 *  - Wide / landscape  -> a two-page spread: illustration LEFT, narration RIGHT.
 *  - Phones / portrait -> a single stacked page.
 *
 * Turning the page is a single rotating leaf (CSS 3D). On the spread the leaf's
 * front is the CURRENT text and its back is the NEXT illustration; as it turns,
 * the next text is revealed underneath on the right and the next image lands on
 * the left. On a single page the leaf is the current scene lifting to reveal the
 * next one underneath.
 *
 * While a turn is in flight, `pending` holds the next scene so both pages can
 * render at once; the parent commits the swap on `onTurnEnd`. Reduced motion is
 * handled by the parent (it commits instantly and never sets `pending`), so no
 * leaf is rendered and the global `animation: none` rule has nothing to stall.
 */
// Must match app.css's `.scene` spread breakpoint exactly, so the JS-chosen
// layout and the CSS rules never disagree.
const SPREAD_QUERY = "(min-width: 820px)";
/** Minimum horizontal travel for a swipe-left to count as a forward page turn. */
const SWIPE_THRESHOLD = 48;

export function BookView({
  scene,
  pending,
  currentMs,
  assetBase,
  onTurnEnd,
  onSwipeForward,
}: {
  /** The current (committed) scene — the "from" page during a turn. */
  scene: Scene;
  /** The next scene while a page-turn is in flight; null when settled. */
  pending: Scene | null;
  currentMs: number;
  assetBase: string;
  /** Commit the turn; fired on the leaf's own animationend. */
  onTurnEnd: () => void;
  /** Swipe-left to advance. Undefined disables it (choice scenes / mid-turn). */
  onSwipeForward?: () => void;
}) {
  const isSpread = useMediaQuery(SPREAD_QUERY);
  const turning = pending !== null;
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const className = [
    "book",
    isSpread ? "book--spread" : "book--single",
    turning ? "book--turning" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={className}
      onTouchStart={(e) => {
        const t = e.touches[0];
        touchStart.current = t ? { x: t.clientX, y: t.clientY } : null;
      }}
      onTouchEnd={(e) => {
        const start = touchStart.current;
        touchStart.current = null;
        if (!start || !onSwipeForward) return;
        const t = e.changedTouches[0];
        if (!t) return;
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        // a mostly-horizontal left swipe turns the page forward
        if (dx < -SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) onSwipeForward();
      }}
    >
      {isSpread ? (
        <Spread
          from={scene}
          to={pending}
          currentMs={currentMs}
          assetBase={assetBase}
          onTurnEnd={onTurnEnd}
        />
      ) : (
        <Single
          from={scene}
          to={pending}
          currentMs={currentMs}
          assetBase={assetBase}
          onTurnEnd={onTurnEnd}
        />
      )}
    </div>
  );
}

type PageProps = {
  from: Scene;
  to: Scene | null;
  currentMs: number;
  assetBase: string;
  onTurnEnd: () => void;
};

/** Only the leaf's OWN animation commits the turn (ignore any descendant anims). */
function leafEnd(onTurnEnd: () => void) {
  return (e: { target: EventTarget; currentTarget: EventTarget }) => {
    if (e.target === e.currentTarget) onTurnEnd();
  };
}

function Spread({ from, to, currentMs, assetBase, onTurnEnd }: PageProps) {
  return (
    <>
      {/* LEFT page: illustration. During a turn it shows the current image until
          the leaf's back (next image) lands on top of it. */}
      <div className="book-page book-page--left">
        <Illustration
          image={from.image}
          assetBase={assetBase}
          segments={from.audio?.segments}
          currentMs={currentMs}
          progress={sceneProgress(from, currentMs)}
        />
      </div>

      <div className="book-spine" aria-hidden="true" />

      {/* RIGHT page: narration. During a turn this is the NEXT text (un-highlighted),
          revealed as the leaf lifts; settled, it's the current text. Keyed by
          scene id so a new page's line replays its word-order settle. */}
      <div className="book-page book-page--right">
        <NarrationText
          key={(to ?? from).id}
          scene={to ?? from}
          currentMs={to ? 0 : currentMs}
        />
      </div>

      {to && (
        <div className="leaf leaf--spread" onAnimationEnd={leafEnd(onTurnEnd)}>
          <div className="leaf-face leaf-front">
            <div className="book-page book-page--right">
              <NarrationText key={from.id} scene={from} currentMs={currentMs} />
            </div>
          </div>
          <div className="leaf-face leaf-back">
            <div className="book-page book-page--left">
              <Illustration image={to.image} assetBase={assetBase} />
            </div>
          </div>
          <div className="leaf-shadow" aria-hidden="true" />
        </div>
      )}
    </>
  );
}

function Single({ from, to, currentMs, assetBase, onTurnEnd }: PageProps) {
  return (
    <>
      {/* Base card: during a turn this is the NEXT scene, revealed as the page
          lifts; settled, it's the current scene. */}
      <div className="book-page book-page--full">
        <ScenePlayer
          scene={to ?? from}
          currentMs={to ? 0 : currentMs}
          assetBase={assetBase}
        />
      </div>

      {to && (
        <div className="leaf leaf--single" onAnimationEnd={leafEnd(onTurnEnd)}>
          <div className="leaf-face leaf-front">
            <div className="book-page book-page--full">
              <ScenePlayer scene={from} currentMs={currentMs} assetBase={assetBase} />
            </div>
          </div>
          <div className="leaf-face leaf-back leaf-back--blank" aria-hidden="true" />
          <div className="leaf-shadow" aria-hidden="true" />
        </div>
      )}
    </>
  );
}
